import { useEffect, useState } from 'react';
import { Button, Progress } from 'antd';
import { CheckCircleIcon } from '../../assets/icons';
import type { PlanMode, ProgressEvent } from '../../features/aiPlanner/plannerApi';

interface Props {
  mode: PlanMode;
  events: ProgressEvent[];
  startedAt?: string | null;
  failed?: string | null;
  onRetry?: () => void;
}

const TITLE: Record<PlanMode, string> = {
  plan: 'Making your plan',
  add_program: 'Working out the new tasks',
  remove_program: 'Working out what changes',
};

const TIPS: Record<PlanMode, string[]> = {
  plan: [
    'Every task can be edited, removed or reassigned before anything is created.',
    'You can talk to the AI on the next screen: "give the food tasks to Sudhir", "move the rehearsal to the 17th".',
    'Dates are counted from the event date; change the date and every deadline follows.',
    'Tasks that spend money or need the leader are marked "needs confirmation".',
  ],
  add_program: [
    'Existing tasks are only changed where a number, a date or a person really changes.',
    'Completed and in-progress tasks are never touched. They are only marked for you to decide.',
    'You can refine the proposal in chat before applying it.',
  ],
  remove_program: [
    'Tasks that still serve the rest of the event are shrunk, not removed.',
    'Completed and in-progress tasks are never deleted. They are shown on a red background instead.',
    'Nothing changes in the engagement until you press Apply.',
  ],
};

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/** Live timeline of the planner's stages while the session is 'drafting'. */
export default function ProgressPanel({ mode, events, startedAt, failed, onRetry }: Props) {
  const [elapsed, setElapsed] = useState(0);
  const [tip, setTip] = useState(0);
  const last = events[events.length - 1];
  const pct = failed ? (last?.pct ?? 0) : Math.max(3, last?.pct ?? 3);
  const finished = !!last?.done && !failed;

  useEffect(() => {
    const t0 = startedAt ? new Date(startedAt).getTime() : Date.now();
    const id = window.setInterval(() => setElapsed(Math.max(0, Math.round((Date.now() - t0) / 1000))), 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);
  useEffect(() => {
    const id = window.setInterval(() => setTip((i) => i + 1), 7000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="mx-auto flex max-w-[640px] flex-col gap-5 py-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h3 className="m-0 text-base font-semibold text-ink">{failed ? 'The planner could not finish' : finished ? 'Done' : TITLE[mode]}</h3>
          <p className="m-0 mt-0.5 text-sm text-ink-secondary">
            {failed ? failed : finished ? 'Opening the plan…' : 'This usually takes a minute or two. Please keep this window open.'}
          </p>
        </div>
        <span className="shrink-0 font-mono text-sm tabular-nums text-ink-secondary">{fmt(elapsed)}</span>
      </div>

      <Progress percent={pct} showInfo={false} status={failed ? 'exception' : finished ? 'success' : 'active'}
        strokeColor={failed ? undefined : { from: '#0061FF', to: '#4D8BFF' }} size={['100%', 8]} />

      <ol className="m-0 flex list-none flex-col gap-0 p-0">
        {events.map((e, i) => {
          const isLast = i === events.length - 1;
          const running = isLast && !e.done && !failed;
          const isFail = e.stage === 'failed';
          return (
            <li key={i} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span className={['mt-1 flex size-5 shrink-0 items-center justify-center rounded-full',
                  isFail ? 'bg-[#FEE2E2] text-[#DC2626]'
                    : running ? 'bg-brand-shade' : 'bg-[#DCFCE7] text-[#16A34A]'].join(' ')}>
                  {running ? <span className="size-2.5 animate-pulse rounded-full bg-brand" />
                    : isFail ? <span className="text-xs font-bold">!</span>
                      : <CheckCircleIcon className="size-4" />}
                </span>
                {!isLast && <span className="my-0.5 w-px flex-1 bg-line-soft" style={{ minHeight: 14 }} />}
              </div>
              <div className={['pb-3 text-sm leading-6', running ? 'font-medium text-ink' : isFail ? 'text-[#B91C1C]' : 'text-ink-secondary'].join(' ')}>
                {e.text}{running && <span className="inline-block w-4 animate-pulse">…</span>}
              </div>
            </li>
          );
        })}
      </ol>

      {failed ? (
        onRetry && <div><Button type="primary" onClick={onRetry}>Start again</Button></div>
      ) : !finished && (
        <div className="rounded-lg border border-line-soft bg-bg/60 px-4 py-3 text-xs leading-5 text-ink-secondary">
          <span className="font-medium text-ink">While you wait: </span>
          {TIPS[mode][tip % TIPS[mode].length]}
        </div>
      )}
    </div>
  );
}
